import { useState } from "react";
import { industryAds, featuredAds } from "@/mocks/ads";

const badgeColors: Record<string, string> = {
  Featured: "bg-amber-500 text-white",
  Hot: "bg-red-500 text-white",
  New: "bg-green-500 text-white",
  Popular: "bg-orange-500 text-white",
};

const allCategories = ["All", "Real Estate", "Finance", "Marketing", "Technology", "HR Tech", "Education", "Food & Beverage", "Trade", "Automotive", "E-commerce", "Legal"];

export default function IndustryAdsSection() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [visibleCount, setVisibleCount] = useState(6);

  const filtered = activeCategory === "All"
    ? industryAds
    : industryAds.filter((a) => a.category === activeCategory);

  const visible = filtered.slice(0, visibleCount);

  return (
    <section id="ads" className="py-16 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <span className="text-amber-500 text-sm font-semibold uppercase tracking-wider">Sponsored</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">Many Industry Advertisements</h2>
            <p className="text-gray-500 text-sm mt-1">Discover premium business opportunities across all industries</p>
          </div>
          <button className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors cursor-pointer whitespace-nowrap w-fit">
            + Place Your Ad
          </button>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {allCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setVisibleCount(6); }}
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-amber-500 text-white"
                  : "bg-white border border-stone-200 text-stone-600 hover:border-amber-300 hover:text-amber-600"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Featured Ad Banners */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {featuredAds.map((ad) => (
            <div key={ad.id} className="relative rounded-xl overflow-hidden cursor-pointer group h-36">
              <img
                src={ad.image}
                alt={ad.title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/65 to-transparent flex items-center px-6">
                <div>
                  <h3 className="text-white font-bold text-base md:text-lg mb-1">{ad.title}</h3>
                  <p className="text-white/75 text-xs mb-3">{ad.subtitle}</p>
                  <button className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-4 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                    {ad.cta}
                  </button>
                </div>
              </div>
              <span className="absolute top-3 right-3 bg-black/40 text-white text-xs px-2 py-0.5 rounded">Sponsored</span>
            </div>
          ))}
        </div>

        {/* Industry Ad Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-6">
          {visible.map((ad) => (
            <div
              key={ad.id}
              className="bg-white rounded-xl overflow-hidden border border-stone-100 hover:border-amber-200 hover:shadow-sm transition-all duration-200 cursor-pointer group"
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={ad.image}
                  alt={ad.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <span className={`absolute top-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full ${badgeColors[ad.badge] || "bg-stone-500 text-white"}`}>
                  {ad.badge}
                </span>
                <span className="absolute top-3 right-3 bg-black/40 text-white text-xs px-2 py-0.5 rounded">Ad</span>
              </div>
              <div className="p-4">
                <span className="text-xs text-amber-600 font-medium">{ad.category}</span>
                <h3 className="font-semibold text-gray-900 text-sm mt-1 mb-1">{ad.title}</h3>
                <p className="text-stone-500 text-xs leading-relaxed mb-3 line-clamp-2">{ad.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-stone-400">{ad.company}</span>
                  <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    {ad.cta}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More */}
        {visibleCount < filtered.length && (
          <div className="text-center">
            <button
              onClick={() => setVisibleCount((v) => v + 6)}
              className="inline-flex items-center gap-2 border border-amber-400 text-amber-600 hover:bg-amber-500 hover:text-white font-semibold px-8 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-add-line"></i>
              Load More Ads ({filtered.length - visibleCount} remaining)
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
